import React, { memo, useState } from "react"
import { Paper, List, ListItem, ListItemAvatar, ListItemText, Button, Typography, Box, Grid } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import moment from "moment"
import { useJobUtils } from "../../utils"
import { Avatar, ActionCardInfo, NavLinkItem, TimeAgo, AdornmentIcon, NoItemsMessage } from ".."

import { UserLink } from "../UserLink"
import { ModuleHeading } from "../Headings"

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 16,
    lineHeight: "20px",
    marginBottom: theme.spacing(1),
  },
  item: {
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  content: {
    borderRadius: 4,
    fontSize: 14,
    lineHeight: "18px",
    color: theme.palette.text.main,
    backgroundColor: theme.palette.quote.background,
    marginBottom: theme.spacing(1),
    padding: `5px ${theme.spacing(1)}px`,
  },
  footer: {
    padding: theme.spacing(2),
  },
  more: {
    textTransform: "none",
    fontSize: 14,
    fontWeight: "600",
    lineHeight: "16px",
  },
}))

const Activity = ({
  jobs,
  blockSize = 5,
  heading = "Recent Activity",
  noItemsMessage = "No recent activity",
  hideOnNoItems = true,
}) => {
  const classes = useStyles()
  const { getNavigateToLink } = useJobUtils()
  const [count, setCount] = useState(blockSize)

  const data = []

  if (jobs) {
    jobs.forEach((job) => {
      const jobTitle = <NavLinkItem to={getNavigateToLink(job)} text={job.title} display="inline" />
      if (job.process) {
        job.status.steps.forEach((step) => {
          if (step.completedAt) {
            data.push({
              type: "job_process_step",
              icon: "process",
              action: step.skip ? "skipped" : "completed",
              title: (
                <>
                  <UserLink user={step.completedBy} /> {step.skip ? <>skipped</> : <>completed</>} {step.name} in{" "}
                  {jobTitle}
                </>
              ),
              when: step.completedAt,
              author: step.completedBy,
            })
          }
        })
      } else if (job.status.completed) {
        data.push({
          type: "job_task",
          icon: "task",
          action: "completed",
          title: (
            <>
              <UserLink user={job.status.completedBy} /> completed the task {jobTitle}
            </>
          ),
          when: job.status.completedAt,
          author: job.status.completedBy,
        })
      }
    })
  }

  const handleViewMoreClick = () => {
    setCount(count + blockSize)
  }

  if (hideOnNoItems && (!data || data.length === 0)) return null

  return (
    <>
      {heading && <ModuleHeading>{heading}</ModuleHeading>}
      <Paper elevation={3}>
        <List disablePadding>
          {data && !data.length && <NoItemsMessage>{noItemsMessage}</NoItemsMessage>}
          {data
            .sort((a, b) => moment(b.when).diff(moment(a.when), "second"))
            .slice(0, count < data.length ? count : data.length)
            .map((item, index) => (
              <ActivityListItemMemo key={index} {...item} classes={classes} />
            ))}
        </List>
        {data.length > count && (
          <Grid container justifyContent="center" alignItems="center" className={classes.footer}>
            <Button className={classes.more} color="primary" onClick={handleViewMoreClick}>
              View more
            </Button>
          </Grid>
        )}
      </Paper>
    </>
  )
}

const ActivityListItem = ({ author, title, description, icon, when, classes }) => (
  <ListItem divider alignItems="flex-start" className={classes.item}>
    <ListItemAvatar>
      <Avatar {...author} />
    </ListItemAvatar>
    <ListItemText>
      <Typography variant="h2" className={classes.title}>
        {title}
      </Typography>
      {description && <Box className={classes.content}>"{description}"</Box>}
      <ActionCardInfo>
        <AdornmentIcon name={icon} />
        <TimeAgo date={when} />
      </ActionCardInfo>
    </ListItemText>
  </ListItem>
)

const ActivityListItemMemo = memo(ActivityListItem)

export { Activity }
